import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { PurchaseRules } from '../../types';

interface PurchaseRulesConfigurationProps {
  projectId: number;
  onSave?: () => void;
}

const PurchaseRulesConfiguration: React.FC<PurchaseRulesConfigurationProps> = ({
  projectId,
  onSave
}) => {
  // State management - matches backend schema
  const [rules, setRules] = useState<PurchaseRules>({
    autonomous_enabled: false,
    daily_budget: 500,
    monthly_budget: 2000,
    auto_approve_under: 50,
    require_approval_over: 100,
    allowed_tlds: [],
    max_purchases_per_hour: 10,
    emergency_stop: false
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Form state for comma-separated TLD input
  const [allowedTldsInput, setAllowedTldsInput] = useState('');

  // Helper function to get auth token
  const getAuthToken = useCallback(() => {
    return localStorage.getItem('auth_token');
  }, []);

  // Load existing rules on mount
  const loadRules = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`/api/v1/projects/${projectId}/purchase-rules`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load purchase rules: ${response.statusText}`);
      }

      const data: PurchaseRules = await response.json();

      if (data) {
        setRules(data);
        setAllowedTldsInput(data.allowed_tlds?.join(', ') || '');
      }
    } catch (err: any) {
      console.error('Failed to load purchase rules:', err);
      setError(err.message || 'Failed to load purchase rules');
      toast.error('Failed to load purchase rules');
    } finally {
      setLoading(false);
    }
  }, [projectId, getAuthToken]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  // Parse TLD input string into array
  const parseTldList = (input: string): string[] => {
    if (!input.trim()) return [];

    return input
      .split(',')
      .map(tld => tld.trim())
      .filter(tld => tld.length > 0)
      .map(tld => {
        // Ensure TLD starts with a dot
        if (!tld.startsWith('.')) {
          return `.${tld}`;
        }
        return tld;
      });
  };

  // Validate TLD format
  const validateTldFormat = (tld: string): boolean => {
    const tldRegex = /^\.[a-zA-Z0-9-]+$/;
    return tldRegex.test(tld);
  };

  // Validate form inputs
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate numeric fields
    if (rules.daily_budget !== undefined && rules.daily_budget < 0) {
      errors.daily_budget = 'Daily budget must be 0 or greater';
    }

    if (rules.monthly_budget !== undefined && rules.monthly_budget < 0) {
      errors.monthly_budget = 'Monthly budget must be 0 or greater';
    }

    if (rules.auto_approve_under !== undefined && rules.auto_approve_under < 0) {
      errors.auto_approve_under = 'Auto-approve threshold must be 0 or greater';
    }

    if (rules.require_approval_over !== undefined && rules.require_approval_over < 0) {
      errors.require_approval_over = 'Approval threshold must be 0 or greater';
    }

    if (rules.max_purchases_per_hour !== undefined && rules.max_purchases_per_hour < 0) {
      errors.max_purchases_per_hour = 'Rate limit must be 0 or greater';
    }

    // Validate budget hierarchy
    if (
      rules.monthly_budget !== undefined &&
      rules.daily_budget !== undefined &&
      rules.monthly_budget < rules.daily_budget
    ) {
      errors.monthly_budget = 'Monthly budget should be greater than or equal to daily budget';
    }

    // Validate approval thresholds
    if (
      rules.auto_approve_under !== undefined &&
      rules.require_approval_over !== undefined &&
      rules.auto_approve_under > rules.require_approval_over
    ) {
      errors.auto_approve_under = 'Auto-approve threshold should be less than or equal to approval required threshold';
    }

    // Validate TLD formats
    const allowedTlds = parseTldList(allowedTldsInput);
    const invalidTlds = allowedTlds.filter(tld => !validateTldFormat(tld));
    if (invalidTlds.length > 0) {
      errors.allowed_tlds = `Invalid TLD format: ${invalidTlds.join(', ')}. TLDs should start with a dot (e.g., .com)`;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix validation errors before saving');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      // Prepare rules with parsed TLD list
      const submitRules: PurchaseRules = {
        ...rules,
        allowed_tlds: parseTldList(allowedTldsInput)
      };

      const response = await fetch(`/api/v1/projects/${projectId}/purchase-rules`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitRules)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to save purchase rules: ${response.statusText}`);
      }

      toast.success('Purchase rules saved successfully!');

      if (onSave) {
        onSave();
      }
    } catch (err: any) {
      console.error('Failed to save purchase rules:', err);
      setError(err.message || 'Failed to save purchase rules');
      toast.error(err.message || 'Failed to save purchase rules');
    } finally {
      setSaving(false);
    }
  };

  // Handle numeric input changes
  const handleNumericChange = (field: keyof PurchaseRules, value: string) => {
    const numValue = value === '' ? 0 : parseFloat(value);
    if (isNaN(numValue)) {
      return;
    }
    setRules(prev => ({ ...prev, [field]: numValue }));
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  // Handle boolean changes
  const handleBooleanChange = (field: keyof PurchaseRules, value: boolean) => {
    setRules(prev => ({ ...prev, [field]: value }));
  };

  // Reset to default values
  const handleReset = () => {
    setRules({
      autonomous_enabled: false,
      daily_budget: 500,
      monthly_budget: 2000,
      auto_approve_under: 50,
      require_approval_over: 100,
      allowed_tlds: [],
      max_purchases_per_hour: 10,
      emergency_stop: false
    });
    setAllowedTldsInput('');
    setValidationErrors({});
    toast.success('Reset to default values');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white dark:bg-cap-dark shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-4">
            Purchase Authorization Rules
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Configure safety rules that govern when AI agents can autonomously purchase domains.
            These limits help prevent unexpected charges and maintain control over spending.
          </p>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Master Control */}
          <div className="mb-8">
            <div className="flex items-center justify-between p-4 bg-primary-50 border border-primary-200 rounded-lg">
              <div>
                <label htmlFor="autonomous_enabled" className="text-sm font-medium text-gray-900 dark:text-warm-white">
                  Enable Autonomous Purchasing
                </label>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Allow AI agents to purchase domains automatically within the configured limits
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  id="autonomous_enabled"
                  checked={rules.autonomous_enabled || false}
                  onChange={(e) => handleBooleanChange('autonomous_enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:bg-cap-dark after:border-gray-300 dark:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          </div>

          {/* Budget Limits Section */}
          <div className="space-y-4 mb-8">
            <h3 className="text-md font-medium text-gray-900 dark:text-warm-white">Budget Limits</h3>

            {/* Daily Budget */}
            <div>
              <label htmlFor="daily_budget" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Daily Budget ($)
              </label>
              <div className="mt-1">
                <input
                  type="number"
                  id="daily_budget"
                  min="0"
                  step="0.01"
                  value={rules.daily_budget ?? 0}
                  onChange={(e) => handleNumericChange('daily_budget', e.target.value)}
                  className={`block w-full rounded-md shadow-sm sm:text-sm dark:bg-gray-800 dark:text-warm-white ${
                    validationErrors.daily_budget
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500'
                  }`}
                  placeholder="e.g., 500"
                />
                {validationErrors.daily_budget && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.daily_budget}</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Maximum total spending allowed per day (0 = unlimited)
                </p>
              </div>
            </div>

            {/* Monthly Budget */}
            <div>
              <label htmlFor="monthly_budget" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Monthly Budget ($)
              </label>
              <div className="mt-1">
                <input
                  type="number"
                  id="monthly_budget"
                  min="0"
                  step="0.01"
                  value={rules.monthly_budget ?? 0}
                  onChange={(e) => handleNumericChange('monthly_budget', e.target.value)}
                  className={`block w-full rounded-md shadow-sm sm:text-sm dark:bg-gray-800 dark:text-warm-white ${
                    validationErrors.monthly_budget
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500'
                  }`}
                  placeholder="e.g., 2000"
                />
                {validationErrors.monthly_budget && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.monthly_budget}</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Maximum total spending allowed per month (0 = unlimited)
                </p>
              </div>
            </div>
          </div>

          {/* Approval Thresholds Section */}
          <div className="space-y-4 mb-8">
            <h3 className="text-md font-medium text-gray-900 dark:text-warm-white">Approval Thresholds</h3>

            {/* Auto-Approve Under */}
            <div>
              <label htmlFor="auto_approve_under" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Auto-Approve Under ($)
              </label>
              <div className="mt-1">
                <input
                  type="number"
                  id="auto_approve_under"
                  min="0"
                  step="0.01"
                  value={rules.auto_approve_under ?? 0}
                  onChange={(e) => handleNumericChange('auto_approve_under', e.target.value)}
                  className={`block w-full rounded-md shadow-sm sm:text-sm dark:bg-gray-800 dark:text-warm-white ${
                    validationErrors.auto_approve_under
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500'
                  }`}
                  placeholder="e.g., 50"
                />
                {validationErrors.auto_approve_under && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.auto_approve_under}</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Purchases below this amount are automatically approved
                </p>
              </div>
            </div>

            {/* Require Approval Over */}
            <div>
              <label htmlFor="require_approval_over" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Require Approval Over ($)
              </label>
              <div className="mt-1">
                <input
                  type="number"
                  id="require_approval_over"
                  min="0"
                  step="0.01"
                  value={rules.require_approval_over ?? 0}
                  onChange={(e) => handleNumericChange('require_approval_over', e.target.value)}
                  className={`block w-full rounded-md shadow-sm sm:text-sm dark:bg-gray-800 dark:text-warm-white ${
                    validationErrors.require_approval_over
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500'
                  }`}
                  placeholder="e.g., 100"
                />
                {validationErrors.require_approval_over && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.require_approval_over}</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Purchases above this amount require manual approval
                </p>
              </div>
            </div>
          </div>

          {/* Rate Limiting Section */}
          <div className="space-y-4 mb-8">
            <h3 className="text-md font-medium text-gray-900 dark:text-warm-white">Rate Limiting</h3>

            {/* Max Purchases Per Hour */}
            <div>
              <label htmlFor="max_purchases_per_hour" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Max Purchases Per Hour
              </label>
              <div className="mt-1">
                <input
                  type="number"
                  id="max_purchases_per_hour"
                  min="0"
                  step="1"
                  value={rules.max_purchases_per_hour ?? 0}
                  onChange={(e) => handleNumericChange('max_purchases_per_hour', e.target.value)}
                  className={`block w-full rounded-md shadow-sm sm:text-sm dark:bg-gray-800 dark:text-warm-white ${
                    validationErrors.max_purchases_per_hour
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500'
                  }`}
                  placeholder="e.g., 10"
                />
                {validationErrors.max_purchases_per_hour && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.max_purchases_per_hour}</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Maximum number of purchases allowed per hour (0 = unlimited)
                </p>
              </div>
            </div>
          </div>

          {/* TLD Restrictions Section */}
          <div className="space-y-4">
            <h3 className="text-md font-medium text-gray-900 dark:text-warm-white">TLD Restrictions</h3>

            {/* Allowed TLDs */}
            <div>
              <label htmlFor="allowed_tlds" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Allowed TLDs
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  id="allowed_tlds"
                  value={allowedTldsInput}
                  onChange={(e) => {
                    setAllowedTldsInput(e.target.value);
                    setValidationErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.allowed_tlds;
                      return newErrors;
                    });
                  }}
                  className={`block w-full rounded-md shadow-sm sm:text-sm dark:bg-gray-800 dark:text-warm-white ${
                    validationErrors.allowed_tlds
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500'
                  }`}
                  placeholder=".com, .net, .org, .io"
                />
                {validationErrors.allowed_tlds && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.allowed_tlds}</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Comma-separated list of allowed TLDs. Leave empty to allow all TLDs.
                </p>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="mt-8 flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={handleReset}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reset to Defaults
            </button>
            <button
              type="submit"
              disabled={saving || Object.keys(validationErrors).length > 0}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                'Save Rules'
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PurchaseRulesConfiguration;
